import React, { useCallback, useRef, useState } from "react";
import { ColorResult, SketchPicker } from "react-color";
import { Field, FieldProps, FieldRenderProps } from "react-final-form";
import { Button, ButtonBase, FormControl, FormHelperText, Paper, Popover, Stack, SxProps, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useDebouncedCallback } from "use-debounce";

export type ColorProps = {
  name: string;
  /** When provided, renders the return value as a clickable trigger that opens the picker in a popover with Apply/Cancel. */
  render?: (props: { value: string }) => React.ReactNode;
  fieldProps?: Partial<FieldProps<any, any>>;
  validate?: any;
  /** SxProps to apply to the popover Paper container */
  popoverSx?: SxProps<Theme>;
};

const useCustomStyles = makeStyles<Theme, { color: string }>((theme: Theme) => ({
  picker: {
    padding: "0 !important",
    boxShadow: "none !important",
  },
}));

const ColorField: React.FC<ColorProps> = (props: ColorProps) => {
  const { fieldProps, name, validate, render, popoverSx, ...rest } = props;

  return (
    <Field
      name={name}
      render={({ input, meta }) => (
        <ColorWrapper input={input} meta={meta} name={name} render={render} popoverSx={popoverSx} {...rest} key={input.value} />
      )}
      validate={validate}
      {...fieldProps}
    />
  );
};

type ColorWrapperProps = FieldRenderProps<string, HTMLElement> & Pick<ColorProps, "render" | "popoverSx">;

const ColorWrapper: React.FC<ColorWrapperProps> = ({
  input: { name, onChange, value, ...restInput },
  helperText,
  render,
  popoverSx,
}: ColorWrapperProps) => {
  const [color, setColor] = useState(value);
  const classes = useCustomStyles({ color: value });

  const onChangeDebounce = useDebouncedCallback((updatedColor: string) => {
    onChange(updatedColor);
  }, 500);

  const handleOnChange = useCallback(({ rgb: { r, g, b, a } }: ColorResult) => {
    const newColor = `rgba(${r},${g},${b},${a})`;
    setColor(newColor);
    onChangeDebounce(newColor);
  }, []);

  if (render) {
    return (
      <FormControl data-testid={`${name}-color-field`} {...restInput}>
        <PopoverColorPicker
          name={name}
          color={color}
          render={render}
          onApply={(newColor) => {
            setColor(newColor);
            onChange(newColor);
          }}
          pickerClassName={classes.picker}
          popoverSx={popoverSx}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <FormControl data-testid={`${name}-color-field`} {...restInput}>
      <SketchPicker color={color} width="160" onChange={handleOnChange} presetColors={[]} className={classes.picker} />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

type PopoverColorPickerProps = {
  name: string;
  color: string;
  render: (props: { value: string }) => React.ReactNode;
  onApply: (color: string) => void;
  pickerClassName: string;
  popoverSx?: SxProps<Theme>;
};

const PopoverColorPicker: React.FC<PopoverColorPickerProps> = ({ name, color, render, onApply, pickerClassName, popoverSx }) => {
  const [open, setOpen] = useState(false);
  const [pendingColor, setPendingColor] = useState(color);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleOpen = () => {
    setPendingColor(color);
    setOpen(true);
  };

  const handleApply = () => {
    onApply(pendingColor);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleChange = ({ rgb: { r, g, b, a } }: ColorResult) => {
    setPendingColor(`rgba(${r},${g},${b},${a})`);
  };

  return (
    <>
      <ButtonBase ref={anchorRef} onClick={handleOpen} data-testid={`color-picker-button-${name}`}>
        {render({ value: color })}
      </ButtonBase>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleCancel}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Paper sx={popoverSx}>
          <SketchPicker color={pendingColor} width="160" onChange={handleChange} presetColors={[]} className={pickerClassName} />
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 1 }}>
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Paper>
      </Popover>
    </>
  );
};

export default ColorField;
